import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { mergeStyling } from '../../common-functions';
import { Input as InputPrimitive } from '@base-ui/react';

const Input = ({ className, type, ...props }: React.ComponentProps<'input'>) => {
  return (
    <InputPrimitive
      type={type}
      data-slot="input"
      className={mergeStyling(
        'odin-border-gray-4 focus-visible:odin-border-primary-2 aria-invalid:odin-border-error disabled:odin-bg-gray-5 odin-h-6 odin-border odin-bg-transparent odin-px-2.5 odin-py-1 odin-text-base md:odin-text-sm file:odin-h-6 file:odin-text-sm file:odin-font-medium file:odin-text-gray-1 placeholder:odin-text-gray-3 odin-w-full odin-min-w-0 odin-outline-none file:odin-inline-flex file:odin-border-0 file:odin-bg-transparent disabled:odin-pointer-events-none disabled:odin-cursor-not-allowed disabled:odin-opacity-50',
        className
      )}
      {...props}
    />
  );
};

const Textarea = ({ className, ...props }: React.ComponentProps<'textarea'>) => {
  return (
    <textarea
      data-slot="textarea"
      className={mergeStyling(
        'odin-border-gray-4 focus-visible:odin-border-primary-2 aria-invalid:odin-border-error disabled:odin-bg-gray-5 odin-border odin-bg-transparent odin-px-2.5 odin-py-2 odin-text-base md:odin-text-sm placeholder:odin-text-gray-3 odin-flex field-sizing-content odin-min-h-16 odin-w-full odin-outline-none disabled:odin-cursor-not-allowed disabled:odin-opacity-50',
        className
      )}
      {...props}
    />
  );
};

const InputGroup = ({ className, ...props }: React.ComponentProps<'div'>) => {
  return (
    <div
      data-slot="input-group"
      role="group"
      className={mergeStyling(
        'odin-border-gray-4 has-[[data-slot=input-group-control]:focus-visible]:odin-border-primary-2 has-[[data-slot][aria-invalid=true]]:odin-border-error has-disabled:odin-bg-gray-5 odin-h-6 odin-border in-data-[slot=combobox-content]:focus-within:odin-border-inherit in-data-[slot=combobox-content]:focus-within:odin-ring-0 has-disabled:odin-opacity-50 has-[>[data-align=block-end]]:odin-h-auto has-[>[data-align=block-end]]:odin-flex-col has-[>[data-align=block-start]]:odin-h-auto has-[>[data-align=block-start]]:odin-flex-col has-[>[data-align=block-end]]:[&>input]:odin-pt-3 has-[>[data-align=block-start]]:[&>input]:odin-pb-3 has-[>[data-align=inline-end]]:[&>input]:odin-pr-1.5 has-[>[data-align=inline-start]]:[&>input]:odin-pl-1.5 group/input-group odin-relative odin-flex odin-w-full odin-min-w-0 odin-items-center odin-outline-none has-[>textarea]:odin-h-auto',
        className
      )}
      {...props}
    />
  );
};

const inputGroupAddonVariants = cva(
  "odin-h-auto odin-py-1.5 odin-text-sm odin-font-medium group-data-[disabled=true]/input-group:odin-opacity-50 [&>kbd]:odin-rounded-[calc(var(--radius)-5px)] [&>svg:not([class*='size-'])]:odin-size-4 odin-flex odin-cursor-text odin-items-center odin-justify-center odin-select-none",
  {
    variants: {
      align: {
        'inline-start': 'odin-pl-2 has-[>button]:odin-ml-[-0.3rem] has-[>kbd]:odin-ml-[-0.15rem] odin-order-first',
        'inline-end': 'odin-pr-2 has-[>button]:odin-mr-[-0.3rem] has-[>kbd]:odin-mr-[-0.15rem] odin-order-last',
        'block-start':
          'odin-px-2.5 odin-pt-2 group-has-[>input]/input-group:odin-pt-2 [.border-b]:odin-pb-2 odin-order-first odin-w-full odin-justify-start',
        'block-end':
          'odin-px-2.5 odin-pb-2 group-has-[>input]/input-group:odin-pb-2 [.border-t]:odin-pt-2 odin-order-last odin-w-full odin-justify-start',
      },
    },
    defaultVariants: {
      align: 'inline-start',
    },
  }
);

const InputGroupAddon = ({
  className,
  align = 'inline-start',
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof inputGroupAddonVariants>) => {
  return (
    <div
      role="group"
      data-slot="input-group-addon"
      data-align={align}
      className={mergeStyling(inputGroupAddonVariants({ align }), className)}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button')) {
          return;
        }
        e.currentTarget.parentElement?.querySelector('input')?.focus();
      }}
      {...props}
    />
  );
};

const InputGroupText = ({ className, ...props }: React.ComponentProps<'span'>) => {
  return (
    <span
      className={mergeStyling(
        "odin-text-gray-2 odin-gap-2 odin-text-sm [&_svg:not([class*='size-'])]:odin-size-4 odin-flex odin-items-center [&_svg]:odin-pointer-events-none",
        className
      )}
      {...props}
    />
  );
};

const InputGroupInput = ({ className, ...props }: React.ComponentProps<'input'>) => {
  return (
    <Input
      data-slot="input-group-control"
      className={mergeStyling(
        'odin-flex-1 odin-px-2.5 odin-cursor-pointer odin-border-0 odin-bg-transparent odin-text-sm placeholder:odin-text-gray-3 odin-outline-none disabled:odin-cursor-not-allowed disabled:odin-opacity-50 odin-min-w-0',
        className
      )}
      {...props}
    />
  );
};

const InputGroupTextarea = ({ className, ...props }: React.ComponentProps<'textarea'>) => {
  return (
    <Textarea
      data-slot="input-group-control"
      className={mergeStyling(
        'odin-flex-1 odin-px-2.5 odin-py-2 odin-border-0 odin-bg-transparent odin-text-base md:odin-text-sm placeholder:odin-text-gray-3 odin-outline-none disabled:odin-cursor-not-allowed disabled:odin-opacity-50 odin-resize-none',
        className
      )}
      {...props}
    />
  );
};

export { Input, Textarea, InputGroup, InputGroupAddon, InputGroupText, InputGroupInput, InputGroupTextarea };
